import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Storage from '../utils/storageES.js'
import HomeButton from './HomeButton.jsx'
import GemConstellation from './GemConstellation.jsx'

function Explore({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const [currentView, setCurrentView] = useState('main') // main, countryView, cityView
  const [exploreData, setExploreData] = useState({})
  const [randomCityInfo, setRandomCityInfo] = useState(null)
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [selectedCity, setSelectedCity] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)

  // Photo states for explore items
  const [itemPhotos, setItemPhotos] = useState({})
  const [loadingItemPhotos, setLoadingItemPhotos] = useState({})
  const [countryFlags, setCountryFlags] = useState({})

  // Discover widget states
  const [discoverItem, setDiscoverItem] = useState(null)
  const [discoverFading, setDiscoverFading] = useState(false)
  const [progressPercent, setProgressPercent] = useState(0)

  // List of available country files
  const availableCountries = [
    'austria', 'belgium', 'switzerland', 'denmark', 'finland', 'ireland', 'italia',
    'netherlands', 'portugal', 'england', 'lituania', 'romania', 'bulgaria',
    'moldova', 'slovenia', 'czech_republic', 'belarus', 'malta', 'latvia', 'iceland',
    'bosnia and herzegovina', 'croatia', 'estonia', 'norway', 'vatican city',
    'monaco', 'luxembourg', 'montenegro', 'andorra', 'san marino', 'faroe islands',
    'gibraltar', 'north macedonia', 'liechenstein', 'serbia', 'greenland', 'spain',
    'slovakia', 'ukraine', 'albania', 'france', 'germany', 'poland'
  ]

  const fetchCountryFlag = async (countryFile) => {
    if (countryFlags[countryFile]) return;

    const countryNames = {
      'austria': 'Austria', 'belgium': 'Belgium', 'switzerland': 'Switzerland', 'denmark': 'Denmark',
      'finland': 'Finland', 'ireland': 'Ireland', 'italia': 'Italy', 'netherlands': 'Netherlands',
      'portugal': 'Portugal', 'england': 'England', 'lituania': 'Lithuania', 'romania': 'Romania',
      'bulgaria': 'Bulgaria', 'moldova': 'Moldova', 'slovenia': 'Slovenia', 'czech_republic': 'Czechia',
      'belarus': 'Belarus', 'malta': 'Malta', 'latvia': 'Latvia', 'iceland': 'Iceland',
      'bosnia and herzegovina': 'Bosnia and Herzegovina', 'croatia': 'Croatia', 'estonia': 'Estonia',
      'norway': 'Norway', 'vatican city': 'Vatican City', 'monaco': 'Monaco', 'luxembourg': 'Luxembourg',
      'montenegro': 'Montenegro', 'andorra': 'Andorra', 'san marino': 'San Marino', 'faroe islands': 'the Faroe Islands',
      'gibraltar': 'Gibraltar', 'north macedonia': 'North Macedonia', 'liechenstein': 'Liechtenstein',
      'serbia': 'Serbia', 'greenland': 'Greenland', 'spain': 'Spain', 'slovakia': 'Slovakia',
      'ukraine': 'Ukraine', 'albania': 'Albania', 'france': 'France', 'germany': 'Germany', 'poland': 'Poland'
    };

    try {
      // Special cases for problematic countries
      let searchTerm;
      if (countryFile === 'czech_republic') {
        searchTerm = 'Flag of the Czech Republic';
      } else if (countryFile === 'denmark') {
        searchTerm = 'Dannebrog';
      } else if (countryFile === 'netherlands') {
        searchTerm = 'Flag of the Netherlands';
      } else {
        searchTerm = 'Flag of ' + countryNames[countryFile];
      }

      const wikiUrl = 'https://en.wikipedia.org/w/api.php?action=query&format=json&origin=*&prop=pageimages&titles=' + encodeURIComponent(searchTerm) + '&pithumbsize=60&piprop=thumbnail';

      const response = await fetch(wikiUrl);
      const data = await response.json();
      const pages = data.query?.pages;

      if (pages) {
        const pageId = Object.keys(pages)[0];
        const page = pages[pageId];
        const flagUrl = page.thumbnail?.source;

        if (flagUrl) {
          setCountryFlags(prev => ({ ...prev, [countryFile]: flagUrl }));
        }
      }
    } catch (error) {
      console.error('Error fetching flag for ' + countryFile, error);
    }
  };

  useEffect(() => {
    loadRandomDiscoverItem()
    // Fetch flags for all countries when component loads
    availableCountries.forEach((countryFile, index) => {
      setTimeout(() => {
        fetchCountryFlag(countryFile);
      }, index * 100); // Stagger requests
    });

    // Progress bar animation - updates every 100ms
    const progressInterval = setInterval(() => {
      setProgressPercent(prev => {
        if (prev >= 100) return 0
        return prev + (100 / 120) // 12000ms / 100ms = 120 steps
      })
    }, 100)

    // Auto-rotate discover item every 12 seconds
    const rotationInterval = setInterval(() => {
      setProgressPercent(0) // Reset progress
      loadRandomDiscoverItem()
    }, 12000) // 12 seconds

    return () => {
      clearInterval(rotationInterval)
      clearInterval(progressInterval)
    }
  }, [])

  // Enhanced photo fetching functions (upgraded from Plans.jsx)
  const getWikipediaLanguage = (country) => {
    const languageMap = {
      'spain': 'es', 'españa': 'es', 'Spain': 'es', 'España': 'es',
      'france': 'fr', 'francia': 'fr', 'France': 'fr', 'Francia': 'fr',
      'italy': 'it', 'italia': 'it', 'Italy': 'it', 'Italia': 'it',
      'germany': 'de', 'deutschland': 'de', 'alemania': 'de', 'Germany': 'de', 'Deutschland': 'de',
      'portugal': 'pt', 'Portugal': 'pt',
      'netherlands': 'nl', 'nederland': 'nl', 'países bajos': 'nl', 'Netherlands': 'nl', 'Nederland': 'nl',
      'belgium': 'nl', 'belgië': 'nl', 'belgique': 'fr', 'Belgium': 'nl', 'België': 'nl',
      'switzerland': 'de', 'schweiz': 'de', 'suisse': 'fr', 'svizzera': 'it', 'Switzerland': 'de',
      'austria': 'de', 'österreich': 'de', 'Austria': 'de', 'Österreich': 'de',
      'czech republic': 'cs', 'česká republika': 'cs', 'Czech Republic': 'cs',
      'croatia': 'hr', 'hrvatska': 'hr', 'Croatia': 'hr', 'Hrvatska': 'hr',
      'denmark': 'da', 'danmark': 'da', 'Denmark': 'da', 'Danmark': 'da',
      'finland': 'fi', 'suomi': 'fi', 'Finland': 'fi', 'Suomi': 'fi',
      'norway': 'no', 'norge': 'no', 'Norway': 'no', 'Norge': 'no',
      'sweden': 'sv', 'sverige': 'sv', 'Sweden': 'sv', 'Sverige': 'sv',
      'poland': 'pl', 'polska': 'pl', 'Poland': 'pl', 'Polska': 'pl',
      'greece': 'el', 'grecia': 'el', 'ελλάδα': 'el', 'Greece': 'el', 'Grecia': 'el',
      'russia': 'ru', 'россия': 'ru', 'Russia': 'ru', 'Россия': 'ru',
      'ireland': 'en', 'irlande': 'en', 'Ireland': 'en', 'Irlande': 'en',
      'iceland': 'is', 'islande': 'is', 'Iceland': 'is', 'Islande': 'is',
      'hungary': 'hu', 'magyarország': 'hu', 'Hungary': 'hu', 'Magyarország': 'hu',
      'romania': 'ro', 'românia': 'ro', 'Romania': 'ro', 'România': 'ro',
      'bulgaria': 'bg', 'българия': 'bg', 'Bulgaria': 'bg', 'България': 'bg',
      'serbia': 'sr', 'srbija': 'sr', 'Serbia': 'sr', 'Srbija': 'sr',
      'slovenia': 'sl', 'slovenija': 'sl', 'Slovenia': 'sl', 'Slovenija': 'sl',
      'slovakia': 'sk', 'slovensko': 'sk', 'Slovakia': 'sk', 'Slovensko': 'sk',
      'lithuania': 'lt', 'lietuva': 'lt', 'Lithuania': 'lt', 'Lietuva': 'lt',
      'latvia': 'lv', 'latvija': 'lv', 'Latvia': 'lv', 'Latvija': 'lv',
      'estonia': 'et', 'eesti': 'et', 'Estonia': 'et', 'Eesti': 'et'
    };
    return languageMap[country?.toLowerCase()] || 'en';
  };

  // Enhanced localized terms for better photo search
  const getLocalizedTerms = (country, itemType) => {
    const termMaps = {
      'spain': {
        castle: ['castillo', 'castell', 'fortaleza', 'alcázar', 'alcazaba', 'torre'],
        museum: ['museo', 'museu', 'casa museo'],
        church: ['iglesia', 'basílica', 'catedral', 'concatedral', 'ermita', 'santuario'],
        building: ['edificio', 'palacio', 'casa', 'torre'],
        monument: ['monumento', 'memorial', 'estatua'],
        food: ['plato', 'comida', 'gastronomía'],
        activity: ['actividad', 'lugar']
      },
      'france': {
        castle: ['château', 'fort', 'forteresse', 'citadelle'],
        museum: ['musée', 'maison'],
        church: ['église', 'cathédrale', 'basilique', 'chapelle'],
        building: ['bâtiment', 'palais', 'hôtel', 'maison'],
        monument: ['monument', 'mémorial', 'statue'],
        food: ['plat', 'cuisine', 'gastronomie'],
        activity: ['activité', 'lieu']
      },
      'italy': {
        castle: ['castello', 'forte', 'fortezza', 'rocca'],
        museum: ['museo', 'casa'],
        church: ['chiesa', 'cattedrale', 'basilica', 'duomo'],
        building: ['edificio', 'palazzo', 'casa', 'torre'],
        monument: ['monumento', 'memoriale', 'statua'],
        food: ['piatto', 'cucina', 'gastronomia'],
        activity: ['attività', 'luogo']
      },
      'germany': {
        castle: ['schloss', 'burg', 'festung'],
        museum: ['museum', 'haus'],
        church: ['kirche', 'kathedrale', 'basilika', 'dom'],
        building: ['gebäude', 'palast', 'haus', 'turm'],
        monument: ['denkmal', 'monument', 'statue'],
        food: ['gericht', 'küche', 'gastronomie'],
        activity: ['aktivität', 'ort']
      },
      'portugal': {
        castle: ['castelo', 'forte', 'fortaleza', 'alcácer'],
        museum: ['museu', 'casa'],
        church: ['igreja', 'catedral', 'basílica', 'santuário'],
        building: ['edifício', 'palácio', 'casa', 'torre'],
        monument: ['monumento', 'memorial', 'estátua'],
        food: ['prato', 'comida', 'gastronomia'],
        activity: ['atividade', 'local']
      }
    };
    return termMaps[country?.toLowerCase()] || {};
  };

  // Determine item type for better search
  const determineItemType = (item, categoryHint = null) => {
    const name = (item.name || '').toLowerCase();
    const localName = (item.localName || '').toLowerCase();
    const description = (item.description || '').toLowerCase();

    // Check category hint first
    if (categoryHint) {
      if (['culturalAttractions', 'monuments'].includes(categoryHint)) {
        if (name.includes('castle') || name.includes('fort') || localName.includes('castell') ||
            localName.includes('castillo') || localName.includes('château') || localName.includes('castello')) {
          return 'castle';
        }
        if (name.includes('church') || name.includes('cathedral') || localName.includes('iglesia') ||
            localName.includes('église') || localName.includes('chiesa') || localName.includes('catedral')) {
          return 'church';
        }
        if (name.includes('museum') || localName.includes('museo') || localName.includes('musée')) {
          return 'museum';
        }
        return 'monument';
      }
      if (categoryHint === 'typicalFoodsCity' || categoryHint === 'typicalFoodsCountry') {
        return 'food';
      }
      if (categoryHint === 'localActivities') {
        return 'activity';
      }
    }

    // Fallback to name-based detection
    if (name.includes('castle') || name.includes('fort')) return 'castle';
    if (name.includes('church') || name.includes('cathedral')) return 'church';
    if (name.includes('museum')) return 'museum';
    if (name.includes('food') || name.includes('dish')) return 'food';

    return 'building';
  };

  // Enhanced search terms generation with aggressive fallbacks
  const generateSearchTerms = (itemName, itemType, country, city, localName = null) => {
    const searchName = localName || itemName;
    const localTerms = getLocalizedTerms(country, itemType);
    const cityName = city || '';

    // Base terms with city context
    const baseTerms = [
      `${searchName} ${cityName}`,
      `${searchName}`,
      searchName
    ];

    // Add localized architectural terms
    const typeTerms = [];
    if (localTerms[itemType]) {
      localTerms[itemType].forEach(localTerm => {
        typeTerms.push(`${searchName} ${localTerm} ${cityName}`);
        typeTerms.push(`${searchName} ${localTerm}`);
      });
    }

    // Add English fallback terms
    const englishTerms = [
      `${searchName} ${itemType} ${cityName}`,
      `${searchName} ${itemType}`
    ];

    // Add aggressive fallbacks for better photo discovery
    const aggressiveFallbacks = [];

    // For food items - try generic food terms
    if (itemType === 'food') {
      aggressiveFallbacks.push(
        `${searchName} recipe`,
        `${searchName} dish`,
        `${searchName} food`,
        `${searchName} cuisine ${cityName}`,
        `${searchName} ${country} food`,
        `traditional ${searchName}`,
        `${searchName} gastronomy`
      );
    }

    // For monuments/buildings - try generic architecture terms
    if (['monument', 'building', 'castle', 'church', 'museum'].includes(itemType)) {
      aggressiveFallbacks.push(
        `${searchName} architecture`,
        `${searchName} building`,
        `${searchName} historic`,
        `${searchName} heritage`,
        `${searchName} tourism`,
        `${searchName} landmark`
      );
    }

    // For activities - try activity-related terms
    if (itemType === 'activity') {
      aggressiveFallbacks.push(
        `${searchName} activity`,
        `${searchName} tourism`,
        `${searchName} attraction`,
        `${searchName} visit`,
        `${searchName} experience`
      );
    }

    // Add word variations (removing common suffixes/prefixes)
    const wordVariations = [];
    if (searchName.length > 6) {
      // Try without common endings
      const commonEndings = ['es', 's', 'a', 'o', 'e', 'ques', 'ias', 'tos'];
      commonEndings.forEach(ending => {
        if (searchName.toLowerCase().endsWith(ending)) {
          const root = searchName.slice(0, -ending.length);
          if (root.length > 3) {
            wordVariations.push(root);
            wordVariations.push(`${root} ${cityName}`);
          }
        }
      });
    }

    // Add simplified versions (first word only for multi-word items)
    const words = searchName.split(' ');
    if (words.length > 1) {
      wordVariations.push(words[0]); // Just first word
      wordVariations.push(`${words[0]} ${cityName}`);
      wordVariations.push(`${words[0]} ${itemType}`);
    }

    const allTerms = [...baseTerms, ...typeTerms, ...englishTerms, ...aggressiveFallbacks, ...wordVariations];

    // Clean up and remove duplicates
    return [...new Set(allTerms.filter(term => term.trim().length > 2))]; // Minimum 3 chars
  };

  const isValidPhotoUrl = (url) => {
    if (!url) return false;
    const urlLower = url.toLowerCase();
    if (urlLower.includes('.svg')) {
      console.log('🚫 Skipping SVG file: ' + url);
      return false;
    }
    const excludedFormats = ['.gif', '.webm', '.mp4', '.pdf', '.doc'];
    if (excludedFormats.some(format => urlLower.includes(format))) {
      return false;
    }
    return true;
  };

  const fetchItemPhoto = async (item, cityName, countryName, categoryHint = null) => {
    if (!item?.name || itemPhotos[item.name] || loadingItemPhotos[item.name]) return;

    setLoadingItemPhotos(prev => ({ ...prev, [item.name]: true }));
    console.log('🖼️ [EXPLORE] Starting enhanced photo fetch for:', item.name, 'localName:', item.localName);

    try {
      const wikiLang = getWikipediaLanguage(countryName);
      const itemType = determineItemType(item, categoryHint);

      console.log('🔍 [EXPLORE] Detected item type:', itemType, 'for country:', countryName);

      // Generate enhanced search terms with localized terms
      const searchTerms = generateSearchTerms(item.name, itemType, countryName, cityName, item.localName);
      console.log('🔍 [EXPLORE] Enhanced search terms for "' + item.name + '":', searchTerms);

      // Try multiple Wikipedia languages for better coverage
      const languagesToTry = [wikiLang, 'en']; // Primary language + English fallback
      if (wikiLang !== 'es' && countryName.toLowerCase().includes('spain')) {
        languagesToTry.push('es'); // Add Spanish for Spain-related content
      }
      if (wikiLang !== 'fr' && countryName.toLowerCase().includes('france')) {
        languagesToTry.push('fr'); // Add French for France-related content
      }

      // Try each search term with multiple languages
      for (const searchTerm of searchTerms) {
        console.log('🔍 [EXPLORE] Trying enhanced search term: "' + searchTerm + '"');

        for (const lang of languagesToTry) {
          const wikiUrl = `https://${lang}.wikipedia.org/w/api.php?action=query&format=json&origin=*&prop=pageimages|pageterms&titles=${encodeURIComponent(searchTerm)}&pithumbsize=300&piprop=thumbnail|name|original`;

          try {
            const response = await fetch(wikiUrl);
            if (!response.ok) continue;

            const data = await response.json();
            const pages = data.query?.pages;
            if (!pages) continue;

            const pageId = Object.keys(pages)[0];
            const page = pages[pageId];

            if (!page || page.missing || parseInt(pageId) < 0) continue;

            let imageUrl = page.thumbnail?.source;
            let originalUrl = page.original?.source;

            if (imageUrl && isValidPhotoUrl(imageUrl)) {
              const photoData = {
                urls: { regular: imageUrl, original: originalUrl || imageUrl },
                src: { medium: imageUrl, large: originalUrl || imageUrl },
                alt_description: item.name,
                user: { name: 'Wikipedia' },
                wikipedia: true
              };

              setItemPhotos(prev => ({ ...prev, [item.name]: photoData }));
              console.log('✅ [EXPLORE] Found photo for ' + item.name + ' with enhanced term "' + searchTerm + '" (lang: ' + lang + '): ' + imageUrl);
              return;
            }
          } catch (error) {
            console.error('❌ [EXPLORE] Error fetching from ' + lang + '.wikipedia for "' + searchTerm + '":', error);
            continue;
          }

          // Small delay between language attempts
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Larger delay between different search terms
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      console.log('❌ [EXPLORE] No photo found for ' + item.name + ' after trying all enhanced variations');
    } catch (error) {
      console.error('❌ [EXPLORE] Error in enhanced fetchItemPhoto for ' + item.name + ':', error);
    } finally {
      setLoadingItemPhotos(prev => ({ ...prev, [item.name]: false }));
    }
  };

  const loadRandomDiscoverItem = async () => {
    try {
      const randomCountry = availableCountries[Math.floor(Math.random() * availableCountries.length)]

      // Determine language folder based on current i18n language
      let langFolder = '';
      if (i18n.language === 'es' || i18n.language === 'es-ES') {
        langFolder = 'es/';
      } else if (i18n.language === 'fr' || i18n.language === 'fr-FR') {
        langFolder = 'fr/';
      } else {
        langFolder = 'en/';
      }

      const response = await fetch(`./TRIP/${langFolder}${randomCountry}.json`)
      if (response.ok) {
        const data = await response.json()
        if (data.cities && data.cities.length > 0) {
          const randomCity = data.cities[Math.floor(Math.random() * data.cities.length)]

          // Collect all available items from the 4 categories
          const categories = []

          if (randomCity.typicalFoodsCity && randomCity.typicalFoodsCity.length > 0) {
            categories.push({
              type: 'Local Food',
              emoji: '🍽️',
              color: '#E74C3C',
              bgColor: '#fdf2f2',
              items: randomCity.typicalFoodsCity,
              categoryHint: 'typicalFoodsCity'
            })
          }

          if (randomCity.typicalFoodsCountry && randomCity.typicalFoodsCountry.length > 0) {
            categories.push({
              type: 'National Cuisine',
              emoji: '🥘',
              color: '#F39C12',
              bgColor: '#fef9f2',
              items: randomCity.typicalFoodsCountry,
              categoryHint: 'typicalFoodsCountry'
            })
          }

          if (randomCity.culturalAttractions && randomCity.culturalAttractions.length > 0) {
            categories.push({
              type: 'Cultural Attraction',
              emoji: '🎭',
              color: '#9B59B6',
              bgColor: '#f8f6fb',
              items: randomCity.culturalAttractions,
              categoryHint: 'culturalAttractions'
            })
          }

          if (randomCity.localActivities && randomCity.localActivities.length > 0) {
            categories.push({
              type: 'Local Activity',
              emoji: '⚡',
              color: '#3498DB',
              bgColor: '#f0f8ff',
              items: randomCity.localActivities,
              categoryHint: 'localActivities'
            })
          }

          if (randomCity.monuments && randomCity.monuments.length > 0) {
            categories.push({
              type: 'Monument & Landmark',
              emoji: '🏛️',
              color: '#34495E',
              bgColor: '#f7f8fa',
              items: randomCity.monuments,
              categoryHint: 'monuments'
            })
          }

          // Pick a random category and then a random item from it
          if (categories.length > 0) {
            const randomCategory = categories[Math.floor(Math.random() * categories.length)]
            const randomItem = randomCategory.items[Math.floor(Math.random() * randomCategory.items.length)]

            const discoverData = {
              item: randomItem,
              category: randomCategory.type,
              emoji: randomCategory.emoji,
              color: randomCategory.color,
              bgColor: randomCategory.bgColor,
              categoryHint: randomCategory.categoryHint,
              city: randomCity.city,
              country: data.country,
              countryFile: randomCountry
            }

            setDiscoverItem(discoverData)

            // Fetch photo for this item
            fetchItemPhoto(randomItem, randomCity.city, data.country, randomCategory.categoryHint)
          }
        }
      }
    } catch (error) {
      console.error('Error loading random discover item:', error)
    }
  }

  const loadCountryData = async (countryFile) => {
    setLoading(true)
    try {
      // Determine language folder based on current i18n language
      let langFolder = '';
      if (i18n.language === 'es' || i18n.language === 'es-ES') {
        langFolder = 'es/';
      } else if (i18n.language === 'fr' || i18n.language === 'fr-FR') {
        langFolder = 'fr/';
      } else {
        langFolder = 'en/';
      }

      const response = await fetch(`./TRIP/${langFolder}${countryFile}.json`)
      if (response.ok) {
        const data = await response.json()
        setExploreData(data)
        setSelectedCountry(data)
        setCurrentView('countryView')
      }
    } catch (error) {
      console.error('Error loading country data:', error)
    }
    setLoading(false)
  }

  const handleCitySelection = (city) => {
    setSelectedCity(city)
    setCurrentView('cityView')
  }

  // Load photos when city is selected
  useEffect(() => {
    if (selectedCity && selectedCountry) {
      console.log('📸 Loading photos for city: ' + selectedCity.city);

      // Load photos for all activity types with category tracking
      const allItems = [];

      // Add food items with category hint
      if (selectedCity.typicalFoodsCity) {
        selectedCity.typicalFoodsCity.slice(0, 6).forEach(item => {
          allItems.push({ ...item, categoryHint: 'typicalFoodsCity' });
        });
      }
      if (selectedCity.typicalFoodsCountry) {
        selectedCity.typicalFoodsCountry.slice(0, 6).forEach(item => {
          allItems.push({ ...item, categoryHint: 'typicalFoodsCountry' });
        });
      }

      // Add cultural attractions with category hint
      if (selectedCity.culturalAttractions) {
        selectedCity.culturalAttractions.slice(0, 6).forEach(item => {
          allItems.push({ ...item, categoryHint: 'culturalAttractions' });
        });
      }

      // Add monuments with category hint
      if (selectedCity.monuments) {
        selectedCity.monuments.slice(0, 6).forEach(item => {
          allItems.push({ ...item, categoryHint: 'monuments' });
        });
      }

      // Add local activities with category hint
      if (selectedCity.localActivities) {
        selectedCity.localActivities.slice(0, 6).forEach(item => {
          allItems.push({ ...item, categoryHint: 'localActivities' });
        });
      }

      // Fetch photos for each item with enhanced search and staggered requests
      allItems.forEach((item, index) => {
        setTimeout(() => {
          fetchItemPhoto(item, selectedCity.city, selectedCountry.country, item.categoryHint);
        }, index * 500); // 500ms delay between requests
      });
    }
  }, [selectedCity, selectedCountry])

  const handleNextDiscover = () => {
    setProgressPercent(0) // Reset progress bar
    loadRandomDiscoverItem()
  }

  const DiscoverWidget = () => {
    if (!discoverItem) return (
      <div style={{
        background: 'linear-gradient(135deg, #D4A574 0%, #B8A082 100%)',
        borderRadius: '20px',
        padding: '24px',
        color: 'white',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        textAlign: 'center'
      }}>
        {t('explore.loading')}
      </div>
    )

    const item = discoverItem.item
    const photo = itemPhotos[item.name]
    const isLoadingPhoto = loadingItemPhotos[item.name]

    return (
      <div style={{
        background: 'linear-gradient(135deg, #D4A574 0%, #B8A082 100%)',
        borderRadius: '20px',
        padding: '24px',
        color: 'white',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Progress Bar */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'rgba(255,255,255,0.2)',
          zIndex: 1
        }}>
          <div style={{
            height: '100%',
            width: `${progressPercent}%`,
            background: 'rgba(255,255,255,0.8)',
            transition: 'width 0.1s linear',
            boxShadow: '0 0 10px rgba(255,255,255,0.5)'
          }}></div>
        </div>

        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '16px',
          marginTop: '4px'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '12px',
              padding: '6px 12px',
              marginBottom: '8px'
            }}>
              <span style={{ marginRight: '6px' }}>{discoverItem.emoji}</span>
              <span style={{
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>{discoverItem.category}</span>
            </div>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              margin: '0',
              opacity: '0.9'
            }}>{discoverItem.city}, {discoverItem.country}</h3>
          </div>
        </div>

        {/* Photo */}
        <div style={{
          height: '200px',
          width: '100%',
          marginBottom: '16px',
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: 'rgba(255,255,255,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {photo ? (
            <img
              src={photo.urls.regular}
              alt={item.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          ) : isLoadingPhoto ? (
            <div style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.7)'
            }}>
              {t('explore.loadingPhoto')}
            </div>
          ) : (
            <div style={{
              fontSize: '48px'
            }}>
              {discoverItem.emoji}
            </div>
          )}
        </div>

        {/* Item Info */}
        <div style={{
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <h4 style={{
            fontSize: '18px',
            fontWeight: '700',
            margin: '0 0 8px 0'
          }}>{item.name}</h4>
          {item.localName && item.localName !== item.name && (
            <p style={{
              fontSize: '13px',
              margin: '0 0 8px 0',
              opacity: '0.8',
              fontStyle: 'italic'
            }}>{item.localName}</p>
          )}
          <p style={{
            fontSize: '14px',
            lineHeight: '1.5',
            margin: '0',
            opacity: '0.95'
          }}>{item.description}</p>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px'
        }}>
          <button
            onClick={handleNextDiscover}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '2px solid rgba(255,255,255,0.3)',
              borderRadius: '12px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              flex: '0 0 auto'
            }}
          >
            🎲 {t('explore.next')}
          </button>
          <button
            onClick={() => loadCountryData(discoverItem.countryFile)}
            style={{
              background: 'white',
              color: '#8B7355',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              flex: '1'
            }}
          >
            🗺️ {t('explore.exploreCountry')} {discoverItem.country}
          </button>
        </div>
      </div>
    )
  }

  const CountryView = () => {
    if (!selectedCountry) return <div>{t('explore.loading')}</div>

    return (
      <div>
        {/* Country Header */}
        <div style={{
          background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b6b 100%)',
          borderRadius: '20px',
          padding: '24px',
          color: 'white',
          marginBottom: '24px',
          position: 'relative'
        }}>
          {/* Back Button inside header */}
          <button
            onClick={() => {
              setCurrentView('main')
              setSelectedCountry(null)
            }}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(255,255,255,0.9)',
              color: '#8B7355',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
            }}
          >
            {t('explore.back')}
          </button>

          <div style={{
            marginBottom: '16px'
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              margin: '0',
              textAlign: 'center'
            }}>{selectedCountry.country}</h2>
          </div>
          <p style={{
            fontSize: '16px',
            opacity: '0.9',
            margin: '0'
          }}>{selectedCountry.cities?.length || 0} {t('explore.citiesAvailable')}</p>
        </div>

        {/* Cities Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '20px'
        }}>
          {selectedCountry.cities?.map((city, index) => (
            <div
              key={index}
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '20px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                cursor: 'pointer'
              }}
              onClick={() => {
                setSelectedCity(city)
                setCurrentView('cityView')
              }}
            >
              <h3 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#8B7355',
                margin: '0 0 12px 0'
              }}>{city.city}</h3>

              <p style={{
                fontSize: '14px',
                color: '#666',
                lineHeight: '1.5',
                margin: '0 0 16px 0'
              }}>{city.briefDescription?.slice(0, 120)}...</p>

              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '16px'
              }}>
                {city.typicalFoodsCity && (
                  <span style={{
                    background: '#E74C3C',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}>🍴 {city.typicalFoodsCity.length} Food</span>
                )}
                {city.culturalAttractions && (
                  <span style={{
                    background: '#9B59B6',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}>🎭 {city.culturalAttractions.length} Cultural</span>
                )}
                {city.monuments && (
                  <span style={{
                    background: '#34495E',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}>🏛️ {city.monuments.length} Monuments</span>
                )}
              </div>

              <div style={{
                fontSize: '14px',
                color: '#B8A082',
                fontWeight: '600'
              }}>
                {t('explore.clickToExplore')}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const CityView = () => {
    if (!selectedCity) return <div>{t('explore.loading')}</div>

    return (
      <div>
        {/* City Header */}
        <div style={{
          background: 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)',
          borderRadius: '20px',
          padding: '24px',
          color: 'white',
          marginBottom: '24px',
          position: 'relative'
        }}>
          {/* Back Button inside header */}
          <button
            onClick={() => {
              setCurrentView('countryView')
              setSelectedCity(null)
            }}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'rgba(255,255,255,0.9)',
              color: '#8B7355',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
            }}
          >
            {t('explore.back')}
          </button>

          <div style={{
            marginBottom: '16px'
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              margin: '0',
              textAlign: 'center'
            }}>{selectedCity.city}</h2>
          </div>
          <p style={{
            fontSize: '16px',
            opacity: '0.9',
            margin: '0'
          }}>{selectedCity.briefDescription}</p>
        </div>

        {/* City Information Sections */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* Sprint 4A — sober teaser: this city hides gems you won't find in any
              guide. Shows how many exist (lit cross-trip), never where. Renders
              nothing if the city has no authored gems. */}
          <GemConstellation country={selectedCountry?.country} city={selectedCity.city} variant="explore" />

          {/* Quick Info */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#8B7355',
              margin: '0 0 16px 0'
            }}>{t('explore.quickInfo')}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              {selectedCity.currency && (
                <div>
                  <strong>💰 {t('explore.currency')}</strong> {selectedCity.currency}
                </div>
              )}
              {selectedCity.climate && (
                <div>
                  <strong>🌤️ {t('explore.climate')}</strong> {selectedCity.climate}
                </div>
              )}
              {selectedCity.languagesSpoken?.officialLanguage && (
                <div>
                  <strong>💬 {t('explore.language')}</strong> {selectedCity.languagesSpoken.officialLanguage}
                </div>
              )}
            </div>
          </div>

          {/* Activities Sections */}
          {selectedCity.typicalFoodsCity && selectedCity.typicalFoodsCity.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderLeft: '6px solid #E74C3C'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#E74C3C',
                margin: '0 0 16px 0'
              }}>{t('explore.sections.localFood')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
                {selectedCity.typicalFoodsCity.slice(0, 6).map((item, index) => (
                  <div key={index} style={{
                    background: '#fdf2f2',
                    padding: '12px',
                    borderRadius: '8px'
                  }}>
                    {/* Photo */}
                    <div style={{
                      height: '120px',
                      width: '100%',
                      marginBottom: '8px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {itemPhotos[item.name] ? (
                        <img
                          src={itemPhotos[item.name].urls.regular}
                          alt={item.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : loadingItemPhotos[item.name] ? (
                        <div style={{
                          fontSize: '12px',
                          color: '#666'
                        }}>
                          {t('explore.loading')}
                        </div>
                      ) : (
                        <div style={{
                          fontSize: '24px'
                        }}>
                          🍽️
                        </div>
                      )}
                    </div>
                    <strong>{item.name}</strong>
                    <p style={{ fontSize: '14px', margin: '4px 0 0 0', color: '#666' }}>{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedCity.typicalFoodsCountry && selectedCity.typicalFoodsCountry.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderLeft: '6px solid #F39C12'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#F39C12',
                margin: '0 0 16px 0'
              }}>{t('explore.sections.nationalCuisine')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
                {selectedCity.typicalFoodsCountry.slice(0, 6).map((item, index) => (
                  <div key={index} style={{
                    background: '#fef9f2',
                    padding: '12px',
                    borderRadius: '8px'
                  }}>
                    {/* Photo */}
                    <div style={{
                      height: '120px',
                      width: '100%',
                      marginBottom: '8px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {itemPhotos[item.name] ? (
                        <img
                          src={itemPhotos[item.name].urls.regular}
                          alt={item.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : loadingItemPhotos[item.name] ? (
                        <div style={{
                          fontSize: '12px',
                          color: '#666'
                        }}>
                          {t('explore.loading')}
                        </div>
                      ) : (
                        <div style={{
                          fontSize: '24px'
                        }}>
                          🥘
                        </div>
                      )}
                    </div>
                    <strong>{item.name}</strong>
                    <p style={{ fontSize: '14px', margin: '4px 0 0 0', color: '#666' }}>{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedCity.culturalAttractions && selectedCity.culturalAttractions.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderLeft: '6px solid #9B59B6'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#9B59B6',
                margin: '0 0 16px 0'
              }}>{t('explore.sections.culturalAttractions')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
                {selectedCity.culturalAttractions.slice(0, 6).map((item, index) => (
                  <div key={index} style={{
                    background: '#f8f6fb',
                    padding: '12px',
                    borderRadius: '8px'
                  }}>
                    {/* Photo */}
                    <div style={{
                      height: '120px',
                      width: '100%',
                      marginBottom: '8px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {itemPhotos[item.name] ? (
                        <img
                          src={itemPhotos[item.name].urls.regular}
                          alt={item.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : loadingItemPhotos[item.name] ? (
                        <div style={{
                          fontSize: '12px',
                          color: '#666'
                        }}>
                          {t('explore.loading')}
                        </div>
                      ) : (
                        <div style={{
                          fontSize: '24px'
                        }}>
                          🎭
                        </div>
                      )}
                    </div>
                    <strong>{item.name}</strong>
                    <p style={{ fontSize: '14px', margin: '4px 0 0 0', color: '#666' }}>{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedCity.monuments && selectedCity.monuments.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderLeft: '6px solid #34495E'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#34495E',
                margin: '0 0 16px 0'
              }}>{t('explore.sections.monuments')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
                {selectedCity.monuments.slice(0, 6).map((item, index) => (
                  <div key={index} style={{
                    background: '#f7f8fa',
                    padding: '12px',
                    borderRadius: '8px'
                  }}>
                    {/* Photo */}
                    <div style={{
                      height: '120px',
                      width: '100%',
                      marginBottom: '8px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {itemPhotos[item.name] ? (
                        <img
                          src={itemPhotos[item.name].urls.regular}
                          alt={item.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : loadingItemPhotos[item.name] ? (
                        <div style={{
                          fontSize: '12px',
                          color: '#666'
                        }}>
                          {t('explore.loading')}
                        </div>
                      ) : (
                        <div style={{
                          fontSize: '24px'
                        }}>
                          🏛️
                        </div>
                      )}
                    </div>
                    <strong>{item.name}</strong>
                    <p style={{ fontSize: '14px', margin: '4px 0 0 0', color: '#666' }}>{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedCity.localActivities && selectedCity.localActivities.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              borderLeft: '6px solid #27AE60'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#27AE60',
                margin: '0 0 16px 0'
              }}>{t('explore.sections.localActivities')}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
                {selectedCity.localActivities.slice(0, 6).map((item, index) => (
                  <div key={index} style={{
                    background: '#f1f9f4',
                    padding: '12px',
                    borderRadius: '8px'
                  }}>
                    {/* Photo */}
                    <div style={{
                      height: '120px',
                      width: '100%',
                      marginBottom: '8px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {itemPhotos[item.name] ? (
                        <img
                          src={itemPhotos[item.name].urls.regular}
                          alt={item.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : loadingItemPhotos[item.name] ? (
                        <div style={{
                          fontSize: '12px',
                          color: '#666'
                        }}>
                          {t('explore.loading')}
                        </div>
                      ) : (
                        <div style={{
                          fontSize: '24px'
                        }}>
                          🎯
                        </div>
                      )}
                    </div>
                    <strong>{item.name}</strong>
                    <p style={{ fontSize: '14px', margin: '4px 0 0 0', color: '#666' }}>{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    )
  }

  if (currentView === 'countryView') {
    return (
      <div className="container explore-page" style={{
        background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
        minHeight: '100vh',
        padding: '20px',
        position: 'relative'
      }}>
        {CountryView()}

        {/* Bottom Navigation */}
        <div style={{
          marginTop: '40px',
          paddingTop: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          zIndex: '10000'
        }}>
          {/* Bottom Back to Home Button */}
          <button
            onClick={() => onNavigate('home')}
            style={{
              background: 'transparent',
              color: '#8B7355',
              border: '2px solid #8B7355',
              padding: '12px 24px',
              borderRadius: '25px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← {t('explore.backToHome')}
          </button>

          {/* Bottom Back Button */}
          <button
            onClick={() => {
              setCurrentView('main')
              setSelectedCountry(null)
            }}
            style={{
              background: 'transparent',
              color: '#8B7355',
              border: '2px solid #8B7355',
              padding: '12px 24px',
              borderRadius: '25px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← {t('explore.back')}
          </button>
        </div>
      </div>
    )
  }

  if (currentView === 'cityView') {
    return (
      <div className="container explore-page" style={{
        background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
        minHeight: '100vh',
        padding: '20px',
        position: 'relative'
      }}>
        {CityView()}

        {/* Bottom Navigation */}
        <div style={{
          marginTop: '40px',
          paddingTop: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          zIndex: '10000'
        }}>
          {/* Bottom Back to Home Button */}
          <button
            onClick={() => onNavigate('home')}
            style={{
              background: 'transparent',
              color: '#8B7355',
              border: '2px solid #8B7355',
              padding: '12px 24px',
              borderRadius: '25px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← {t('explore.backToHome')}
          </button>

          {/* Bottom Back Button */}
          <button
            onClick={() => {
              setCurrentView('countryView')
              setSelectedCity(null)
            }}
            style={{
              background: 'transparent',
              color: '#8B7355',
              border: '2px solid #8B7355',
              padding: '12px 24px',
              borderRadius: '25px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← {t('explore.back')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="container explore-page" style={{
      background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
      minHeight: '100vh',
      padding: '20px',
      position: 'relative'
    }}>
      <HomeButton onNavigate={onNavigate} />

      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '30px',
        paddingTop: '60px'
      }}>
        <h1 style={{
          fontSize: '48px',
          fontWeight: '900',
          color: '#8B7355',
          margin: '0 0 8px 0',
          letterSpacing: '2px',
          fontFamily: "'Noto Serif', serif",
          textTransform: 'uppercase'
        }}>{t('explore.title')}</h1>
        <p style={{
          fontSize: '16px',
          color: '#8B7355',
          margin: '0',
          opacity: '0.8'
        }}>{t('explore.subtitle')}</p>
      </div>

      {/* Discover Widget */}
      {DiscoverWidget()}

      {/* Countries Grid */}
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '30px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#8B7355',
          margin: '0 0 20px 0',
          textAlign: 'center'
        }}>🌍 {t('explore.exploreCountries')}</h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: '12px'
        }}>
          {availableCountries.sort().map(countryFile => {
            const countryName = countryFile.charAt(0).toUpperCase() + countryFile.slice(1).replace(/[_-]/g, ' ')
            return (
              <div
                key={countryFile}
                onClick={() => loadCountryData(countryFile)}
                style={{
                  background: '#f9f7f4',
                  borderRadius: '12px',
                  padding: '16px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  border: '2px solid transparent',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#C4714A'
                  e.currentTarget.style.color = 'white'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f9f7f4'
                  e.currentTarget.style.color = '#8B7355'
                }}
              >
                <div style={{
                  width: '40px',
                  height: '30px',
                  margin: '0 auto 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {countryFlags[countryFile] ? (
                    <img
                      src={countryFlags[countryFile]}
                      alt={`Flag of ${countryFile}`}
                      style={{
                        width: '40px',
                        height: '30px',
                        objectFit: 'cover',
                        borderRadius: '2px'
                      }}
                    />
                  ) : (
                    <div style={{ fontSize: '24px' }}>🌍</div>
                  )}
                </div>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#8B7355'
                }}>{countryName}</div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Back Button */}
      <div style={{ textAlign: 'center' }}>
        <button
          style={{
            background: 'transparent',
            color: '#8B7355',
            border: '2px solid #8B7355',
            padding: '12px 24px',
            borderRadius: '25px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
          onClick={() => onNavigate('home')}
        >
          ← {t('explore.backToHome')}
        </button>
      </div>
    </div>
  )
}

export default Explore